import { useQuery } from '@tanstack/react-query'
import { MessageSquare } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { api, MessageItem } from '../api/client'
import { EmptyState } from '../components/ui/Empty'
import { ErrorCard } from '../components/ui/ErrorCard'
import { RowSkeleton } from '../components/ui/Skeleton'
import { Spinner } from '../components/ui/Spinner'
import { useTelegram } from '../hooks/useTelegram'

const PAGE_SIZE = 50

function getDayLabel(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today.getTime() - 86400_000)
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  if (day.getTime() === today.getTime()) return 'Сегодня'
  if (day.getTime() === yesterday.getTime()) return 'Вчера'
  return d.toLocaleDateString('ru', { day: 'numeric', month: 'long' })
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })
}

export function History() {
  const { close } = useTelegram()
  const [cursor, setCursor] = useState<number | null>(null)
  const [allMessages, setAllMessages] = useState<MessageItem[]>([])
  const [hasMore, setHasMore] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)
  const isFirstLoad = useRef(true)

  const url = cursor
    ? `/api/history?limit=${PAGE_SIZE}&before_id=${cursor}`
    : `/api/history?limit=${PAGE_SIZE}`

  const { data, isLoading, error, refetch } = useQuery<MessageItem[]>({
    queryKey: ['history', cursor],
    queryFn: () => api.get(url),
    staleTime: 30_000,
  })

  useEffect(() => {
    if (!data) return
    if (cursor === null) {
      setAllMessages(data)
    } else {
      setAllMessages(prev => [...data, ...prev])
    }
    setHasMore(data.length === PAGE_SIZE)
  }, [data])

  // Открываемся внизу списка, на самом свежем сообщении: история читается
  // как чат, а не как архив с начала времён.
  //
  // Раньше отметка «первая загрузка» тратилась раньше времени. При возврате на
  // экран react-query отдаёт ответ из кеша, поэтому первый же проход эффекта
  // видел isLoading=false и пустой allMessages (состояние заполняется соседним
  // эффектом на следующем проходе) — флаг гасился, а прокрутка не случалась
  // ни тогда, ни потом. Теперь флаг тратится только вместе с самой прокруткой.
  useEffect(() => {
    if (!isFirstLoad.current) return
    if (allMessages.length === 0) return
    isFirstLoad.current = false
    // Через кадр: на момент эффекта пузыри уже в DOM, но высоту им задаёт
    // раскладка, и прокрутка до неё промахивается на длинных сообщениях.
    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'instant', block: 'end' })
    })
  }, [allMessages])

  const loadMore = useCallback(() => {
    if (allMessages.length > 0) {
      setCursor(allMessages[0].id)
    }
  }, [allMessages])

  const isEmpty = !isLoading && !error && allMessages.length === 0

  const groups: { label: string; messages: MessageItem[] }[] = []
  let lastLabel = ''
  for (const msg of allMessages) {
    const label = getDayLabel(msg.created_at)
    if (label !== lastLabel) {
      groups.push({ label, messages: [msg] })
      lastLabel = label
    } else {
      groups[groups.length - 1].messages.push(msg)
    }
  }

  return (
    <div className="flex flex-col min-h-full page-enter">
      <div className="sticky top-0 sticky-header z-10 px-4 pt-5 pb-3">
        <p className="text-h1 text-ink">История</p>
      </div>

      <div className="flex-1 px-4 pb-6">
        {isLoading && cursor === null && (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => <RowSkeleton key={i} />)}
          </div>
        )}

        {error && <ErrorCard onRetry={refetch} />}

        {isEmpty && (
          <EmptyState
            icon={<MessageSquare size={48} />}
            title="История пуста"
            description="Здесь появится переписка с Delo"
            cta="Открыть чат с Delo"
            onCta={close}
          />
        )}

        {/* Load more */}
        {hasMore && (
          <div className="flex justify-center py-3">
            <button
              onClick={loadMore}
              disabled={isLoading}
              className="btn-secondary btn-active text-sm disabled:opacity-40"
              style={{ height: '40px', padding: '0 20px', fontSize: '14px' }}
            >
              {isLoading ? <Spinner className="text-ink-1" /> : null}
              {isLoading ? 'Загрузка...' : 'Загрузить ещё'}
            </button>
          </div>
        )}

        <div className="space-y-1">
          {groups.map(({ label, messages }) => (
            <div key={label}>
              {/* Day divider */}
              <div className="flex items-center gap-3 py-4">
                <div className="flex-1 h-px bg-line" />
                <span className="text-xs font-medium text-ink-2 uppercase tracking-wider shrink-0">
                  {label}
                </span>
                <div className="flex-1 h-px bg-line" />
              </div>

              <div className="space-y-2">
                {messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[82%] px-3.5 py-2.5 ${msg.role === 'user' ? 'bubble-user' : 'bubble-delo'}`}>
                      <p className="text-base leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                      <p className="text-xs mt-1.5 text-ink-2 text-right" style={{ fontSize: '11px' }}>
                        {formatTime(msg.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div ref={bottomRef} />
      </div>
    </div>
  )
}
